import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  CardPurseIcon,
  CardTypesEnum,
  Hint,
  Inscription,
  List,
  OverflowText,
  Toggle,
  useResponsive,
} from '@common-fe/common-fe';

import { ICardPurseDtoLockReasonEnum } from '@/dtos';
import theme from '@/styles/theme';

import { CardPurseAccess } from '../ManageAccountAccessModal.types';

const CARD_PURSE_ICON_SIZE = '32px';
const LOCKED_BY_HEALTH_PLAN_HINT = 'Account is locked by plan rules';
const LOCKED_BY_SYSTEM_HINT = 'Account is locked by the system';

const lockReasonTitles: Record<ICardPurseDtoLockReasonEnum, string> = {
  [ICardPurseDtoLockReasonEnum.REPAYMENT_REQUIRED]: 'Repayment required',
  [ICardPurseDtoLockReasonEnum.DOCUMENTATION_REQUIRED]: 'Documentation required',
  [ICardPurseDtoLockReasonEnum.FRAUD_DETECTED]: 'Fraud detected',
  [ICardPurseDtoLockReasonEnum.CARDHOLDERS_REQUEST]: 'Cardholders request',
};

interface Props {
  purse: CardPurseAccess;
  isLocked: boolean;
  onToggle: (id: string, locked: boolean) => void;
}

export const AccountAccessListItem: React.FC<Props> = ({ purse, isLocked, onToggle }) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();

  const isEmployeeUnlockForbidden = purse.employeeUnlockForbidden;
  const isDisabled = purse.isLockedByHealthPlan || purse.isLockedBySystem || isEmployeeUnlockForbidden;

  const hint = useMemo(() => {
    if (isEmployeeUnlockForbidden) {
      const reasonTitle = purse.lockReason
        ? lockReasonTitles[purse.lockReason as ICardPurseDtoLockReasonEnum]
        : '';
      return `${reasonTitle ? `${reasonTitle}. ` : ''}Account access cannot be modified at this time`;
    }
    if (purse.isLockedByHealthPlan || purse.isLockedBySystem) return LOCKED_BY_HEALTH_PLAN_HINT;
    return LOCKED_BY_SYSTEM_HINT;
  }, [isEmployeeUnlockForbidden, purse.isLockedByHealthPlan, purse.isLockedBySystem, purse.lockReason]);

  const toggleContent = useMemo(
    () => (
      <Box direction="row" align="center" gap="spacing10">
        <Toggle
          testId={`manage-account-access_item_${purse.id}`}
          name="access"
          value={!isLocked}
          twoColorsMode
          disabled={isDisabled}
          onChange={(value) => onToggle(purse.id, !value)}
        />
        <Box style={{ width: '55px', whiteSpace: 'nowrap' }}>
          <Inscription weight={500} color="textSecondary" size="small">
            {t(isLocked ? 'Locked' : 'Unlocked')}
          </Inscription>
        </Box>
      </Box>
    ),
    [isDisabled, isLocked, onToggle, purse.id, t]
  );

  return (
    <List.Item
      direction="row"
      justify="between"
      height="66px"
      testId={`manage-account-access_item_${purse.id}`}
      border={{ color: isLocked && !isDisabled ? 'dangerBorder' : 'border1', size: 'small' }}
      background={isLocked && !isDisabled ? 'dangerContainer' : 'canvas'}
      round="button1Round"
      align="center"
      disableHover
      pad={{ vertical: 'spacing16', horizontal: isMobile ? 'spacing16' : 'spacing24' }}
      style={{ cursor: 'initial' }}
    >
      <Box direction="row" align="center" gap="spacing12" style={{ minWidth: 0 }}>
        <CardPurseIcon type={purse.accountType as CardTypesEnum} size={CARD_PURSE_ICON_SIZE} />
        <Box style={{ width: isMobile ? '120px' : '214px', minWidth: 0 }}>
          <OverflowText
            testId={`manage-account-access_item_${purse.id}_account-name`}
            title={purse.accountName}
            textStyle={{ fontWeight: 'bold', color: theme.colors.textBody }}
          />
        </Box>
      </Box>
      <Box direction="row" align="center">
        {isDisabled ? (
          <Hint hintAlign="vertical" hintElement={toggleContent}>
            <Inscription size="12px" textAlign="center">
              {t(hint)}
            </Inscription>
          </Hint>
        ) : (
          toggleContent
        )}
      </Box>
    </List.Item>
  );
};
